import {getApiConfig} from "../../../config/api.config";
import { getAuthenticationRetryConfig, getFromStorage } from "../../../config/auth.config";
import { logger } from "../../../features/common/logger";
import {IIdentity, userService} from "../../authorization/UsersApi";
import {BaseService} from "../../base-service";
import { HealthIdentifierService } from "../../healthcareidentifier/healthcareidentifier-service";
import { ProviderSnapshot, VerifyHpiiRequest, VerifyHpiiResponse } from "../../healthcareidentifier/models/HpiiItentifierType";
import { ISharedUserInfo, upsertSharedDetailsResponse } from "./models/SharedUserInfo";
import { SignOutEventLog } from "./models/SignOutEventLog";

export enum UserType{
    Doctor = 'Doctor',
    Pharmacist = 'Pharmacist',
    Dentist = 'Dentist',
    GeneralStaffAdmin = 'GeneralStaffAdmin',
    NursePractitioner = 'NursePractitioner',
    Optometrist = 'Optometrist',
    RegisteredNurse = 'RegisteredNurse'
}

export interface IMedUserInfo{
    identity:IIdentity,
    userType:UserType,
    isShared?: boolean,
    userId:string,
    externalUserInfo:IExternalUserInfo,
    isClinicalIdentityProviderUser?: boolean
}

export interface IExternalUserInfo{
  familyName?: string,
  givenName?: string,
  userName?: string
}

export interface IPractitionerHpiiResponse{
    givenName:string,
    familyName:string,
    hpiiNumber:string,
    hpiiStatus:string
}

export enum MapUserToExternalUserType{
  Prescriber,
  Pharmacist
}

//User identity and User info need to be loaded only once
let fetchUserInfoPromise: Promise<any>|null=null;
let identityPromise:Promise<IIdentity>|null=null;
class MedsUserApiV1 {
  get baseUrl() {
    return getApiConfig().url.portal.replace(/\/+$/, "");
  }

  async getMedUserInfo(): Promise<IMedUserInfo> {
    if (!identityPromise) {
      identityPromise = userService.getUserIdentity();
    }

    let identity = await identityPromise;

    let [retryCount, retryWaitTime] = getAuthenticationRetryConfig();

    let data = await this.fetchUserInfo(retryCount, retryWaitTime);    
    return {
      identity,
      userType: data?.userType,
      userId: data?.userId,
      isShared: data?.isSharedAccount,
      isClinicalIdentityProviderUser: data?.isClinicalIdentityProviderUser,
      externalUserInfo: {
        familyName: data?.familyName,
        givenName: data?.givenName,
        userName: data?.externalUserName
      } as IExternalUserInfo,
    } as IMedUserInfo;
  }

  private async fetchUserInfo(
    retryCount?: number,
    retryWaitTime?: number
  ): Promise<any> {
    try {
      if (!fetchUserInfoPromise) {
        let path = "/api/v1/users/me";
        const sharedProviderPath = getFromStorage("providerId");
        if (sharedProviderPath) {
          path = path + "?" + sharedProviderPath;
        }
        fetchUserInfoPromise = await BaseService.AsyncGet(path);
      }

      return await fetchUserInfoPromise;
    } catch (error) {
      if (retryCount) {
        await new Promise((resolve) =>
          setTimeout(resolve, retryWaitTime ?? 6000)
        );
        return this.fetchUserInfo(retryCount - 1, retryWaitTime ?? 6000);
      }
    }
  }

  async singOutEvenLog(signOutEventLog: SignOutEventLog) {
    const path = `/api/v1/users/signout-eventlog`;
    return await BaseService.AsyncPost(path, signOutEventLog);
  }

  async getUserHpiiForFacility(
    encounterAuthoringSystem: string,
    encounterAuthoringSystemForeignKey: string
  ): Promise<IPractitionerHpiiResponse> {
    const path = `/api/v1/users/practitioner-hpii/${encounterAuthoringSystem}/${encounterAuthoringSystemForeignKey}`;
    let response = await BaseService.AsyncGet(path);
    return Promise.resolve(response as IPractitionerHpiiResponse);
  }

  async autoLinkUserToExternalUser(externalUserName: string): Promise<any> {
    const path = `/api/v1/users/auto-link-user`;
    let response = await BaseService.AsyncPost(path, {
      externalUserName,
    });
    return response;
  }

  async verifyHpiiNumber(providerSnapshot: ProviderSnapshot): Promise<any> {
    const user = await this.getMedUserInfo();
    const request: VerifyHpiiRequest = {
      providerSnapshot,
      authoringSystem: user.identity.user.authoringSystem,
      authoringSystemForeignKey: user.identity.user.authoringSystemForeignKey,
      user: {},
      providerIdentifier: {},
    };

    const hpiiPath = `/api/v1/hpii/verify`;
    const response: VerifyHpiiResponse = await HealthIdentifierService.AsyncPost(hpiiPath, request);
    if(response !== null){
      return !response.verificationDetails.length;
    }
    return false;
  }

  async upSertProvider(sharedUser: ISharedUserInfo): Promise<upsertSharedDetailsResponse> {
    if (sharedUser.hpiiNumber) {
      const isValid = await this.verifyHpiiNumber({
        hpii: sharedUser.hpiiNumber,
        familyName: sharedUser.lastName,
        givenName: sharedUser.firstName,
      });      
    }
    const path = `/api/v1/external-users/provider`;
    return await BaseService.AsyncPost(path, sharedUser);
  }

}

const medsUserApi=new MedsUserApiV1();

export {medsUserApi};